import React, { useState, useEffect, useCallback } from 'react';
import useApi from '../../hooks/useApi';
import { X, Star, ShieldAlert } from 'lucide-react';

// --- HELPER PARA MÁSCARA DE CPF ---
const formatCPF = (value) => {
    let v = value.replace(/\D/g, ''); // Remove tudo o que não é dígito
    if (v.length > 11) v = v.slice(0, 11); // Limita a 11 dígitos
    v = v.replace(/(\d{3})(\d)/, '$1.$2'); // Coloca um ponto entre o terceiro e o quarto dígitos
    v = v.replace(/(\d{3})(\d)/, '$1.$2'); // Coloca um ponto entre o terceiro e o quarto dígitos de novo
    v = v.replace(/(\d{3})(\d{1,2})$/, '$1-$2'); // Coloca um hífen entre o terceiro e o quarto dígitos
    return v;
};

const EditUserModal = ({ userId, onClose, onUserUpdated, allCurrencies, currentUser }) => {
    const { apiFetch } = useApi();
    
    // Estados do formulário
    const [username, setUsername] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [recoveryKeyword, setRecoveryKeyword] = useState('');
    const [roles, setRoles] = useState([]);
    const [entities, setEntities] = useState([]); 
    const [selectedRoleId, setSelectedRoleId] = useState('');
    const [status, setStatus] = useState('ativo');
    const [selectedCurrencies, setSelectedCurrencies] = useState([]);
    const [isSuperUser, setIsSuperUser] = useState(false); 
    const [userEntityId, setUserEntityId] = useState(''); 
    
    // --- NOVOS ESTADOS: DADOS PESSOAIS ---
    const [fullName, setFullName] = useState('');
    const [cpf, setCpf] = useState('');
    const [cnh, setCnh] = useState('');

    // FLAG MONITORAMENTO INDIVIDUAL
    const [requiresMonitoring, setRequiresMonitoring] = useState(false);

    // --- NOVOS ESTADOS: ASSINATURA E MULTI-EMPRESA ---
    const [signatureName, setSignatureName] = useState('');
    const [signatureRole, setSignatureRole] = useState('');
    const [signatureRegistry, setSignatureRegistry] = useState('');
    const [isSignaturePublic, setIsSignaturePublic] = useState(false);
    const [signatureImageBase64, setSignatureImageBase64] = useState('');
    const [currentSignatureImage, setCurrentSignatureImage] = useState('');

    // NOVO ESTADO: ENGENHEIRO RESPONSÁVEL
    const [isResponsibleEngineer, setIsResponsibleEngineer] = useState(false);
    
    const [allowedEntities, setAllowedEntities] = useState([]);
    // ----------------------------------
    
    // Estados de controlo da UI
    const [loading, setLoading] = useState(true);
    const [formLoading, setFormLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [userData, rolesData, entitiesData] = await Promise.all([
                apiFetch(`/users/${userId}`),
                apiFetch('/roles'),
                apiFetch('/entities') 
            ]);

            setUsername(userData.username);
            setStatus(userData.status || 'ativo');
            setRoles(rolesData);
            setEntities(entitiesData || []);
            setUserEntityId(userData.entity_id); // CORRIGIDO: snake_case conforme DB
            setSelectedRoleId(userData.role_id); // CORRIGIDO: snake_case conforme DB
            setSelectedCurrencies(userData.allowedCurrencies || []);
            setIsSuperUser(userData.is_super_user); 
            
            // CARREGA DADOS PESSOAIS
            setFullName(userData.full_name || '');
            setCpf(userData.cpf ? formatCPF(userData.cpf) : '');
            setCnh(userData.cnh || '');

            // CARREGA A FLAG DE MONITORAMENTO DO BANCO
            setRequiresMonitoring(userData.requires_monitoring === 1);
            
            // CARREGA OS DADOS DA ASSINATURA E MULTI-EMPRESA
            setSignatureName(userData.signature_name || '');
            setSignatureRole(userData.signature_role || '');
            setSignatureRegistry(userData.signature_registry || '');
            setIsSignaturePublic(userData.is_signature_public === 1 || userData.is_signature_public === true);
            setCurrentSignatureImage(userData.signature_image_path || '');
            setSignatureImageBase64(''); 

            // CARREGA A FLAG DE ENGENHEIRO RESPONSÁVEL
            setIsResponsibleEngineer(userData.is_responsible_engineer === 1 || userData.is_responsible_engineer === true);
            
            setAllowedEntities(userData.allowedEntities || []);
            
            setRecoveryKeyword(''); 
            
        } catch (err) {
            console.error("Erro detalhado ao carregar dados do utilizador:", err);
            setError(`Falha ao carregar dados do utilizador: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [userId, apiFetch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);
    
    const handleCurrencyChange = (currencyCode) => {
        setSelectedCurrencies(prev => 
            prev.includes(currencyCode)
                ? prev.filter(c => c !== currencyCode)
                : [...prev, currencyCode]
        );
    };

    const handleAllowedEntityChange = (entId) => {
        setAllowedEntities(prev => 
            prev.map(String).includes(String(entId)) ? prev.filter(id => String(id) !== String(entId)) : [...prev, String(entId)]
        );
    };

    const handleSignatureFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setSignatureImageBase64(reader.result);
            };
            reader.readAsDataURL(file);
        } else {
            setSignatureImageBase64('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setFormLoading(true);

        const payload = {
            username,
            roleId: selectedRoleId,
            status,
            allowedCurrencies: selectedCurrencies,
            is_super_user: isSuperUser, 
            requires_monitoring: requiresMonitoring, 

            full_name: fullName,
            cpf: cpf,
            cnh: cnh,

            signature_name: signatureName,
            signature_role: signatureRole,
            signature_registry: signatureRegistry,
            is_signature_public: isSignaturePublic,
            is_responsible_engineer: isResponsibleEngineer,
            allowedEntities: isSuperUser ? allowedEntities : []
        };
        
        if (signatureImageBase64) {
            payload.signature_image_base64 = signatureImageBase64;
        }
        
        if (newPassword) payload.password = newPassword;
        if (recoveryKeyword) payload.recoveryKeyword = recoveryKeyword;

        try {
            await apiFetch(`/users/${userId}`, { method: 'PUT', body: payload });
            setSuccess('Utilizador atualizado com sucesso!');
            setTimeout(() => {
                onUserUpdated();
                onClose();
            }, 1000);
        } catch (err) {
            setError(err.message);
        } finally {
            setFormLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div 
                className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4" 
                onClick={e => e.stopPropagation()}
            >
                <div className="p-6 border-b">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-bold">Editar Utilizador</h2>
                        <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="p-10 text-center">A carregar dados do utilizador...</div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                            {error && <div className="p-3 text-red-700 bg-red-100 rounded">{error}</div>}
                            {success && <p className="p-2 mb-4 text-sm text-green-700 bg-green-100 rounded">{success}</p>}
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium">Nome de Utilizador</label>
                                    <input type="text" value={username} onChange={e => setUsername(e.target.value)} required className="w-full p-2 mt-1 border rounded" disabled={username === 'admin'} />
                                </div>
                                <div>
                                    <label className="text-sm font-medium">Função</label>
                                    <select value={selectedRoleId} onChange={e => setSelectedRoleId(e.target.value)} className="w-full p-2 mt-1 border rounded">
                                        {roles.map(role => <option key={role.id} value={role.id}>{role.name}</option>)}
                                    </select>
                                </div>

                                {/* --- NOVOS CAMPOS PESSOAIS --- */}
                                <div className="md:col-span-2">
                                    <label className="text-sm font-medium">Nome Completo</label>
                                    <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} className="w-full p-2 mt-1 border rounded" placeholder="Nome real do usuário" />
                                </div>
                                <div>
                                    <label className="text-sm font-medium">CPF</label>
                                    <input 
                                        type="text" 
                                        value={cpf} 
                                        onChange={e => setCpf(formatCPF(e.target.value))} 
                                        className="w-full p-2 mt-1 border rounded" 
                                        placeholder="000.000.000-00" 
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium">CNH (Opcional)</label>
                                    <input type="text" value={cnh} onChange={e => setCnh(e.target.value)} className="w-full p-2 mt-1 border rounded" placeholder="Número da CNH" />
                                </div>
                                {/* ----------------------------- */}

                                <div>
                                    <label className="text-sm font-medium">Nova Senha</label>
                                    <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Deixar em branco para manter" className="w-full p-2 mt-1 border rounded" />
                                </div>
                                <div>
                                    <label className="text-sm font-medium">Nova Palavra-Chave de Recuperação</label>
                                    <input type="text" value={recoveryKeyword} onChange={e => setRecoveryKeyword(e.target.value)} placeholder="Deixar em branco para manter" className="w-full p-2 mt-1 border rounded" />
                                </div>
                                <div>
                                    <label className="text-sm font-medium">Status</label>
                                    <select 
                                        value={status} 
                                        onChange={e => setStatus(e.target.value)} 
                                        className="w-full p-2 mt-1 border rounded"
                                        disabled={username === 'admin'}
                                        title={username === 'admin' ? "O utilizador admin não pode ser inativado." : ""}
                                    >
                                        <option value="ativo">Ativo</option>
                                        <option value="inativo">Inativo</option>
                                    </select>
                                </div>

                                {!isSuperUser && (
                                    <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-100 rounded">
                                        <input 
                                            id="edit_requires_monitoring"
                                            type="checkbox"
                                            checked={requiresMonitoring}
                                            onChange={(e) => setRequiresMonitoring(e.target.checked)}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <label htmlFor="edit_requires_monitoring" className="text-sm font-bold text-blue-700 flex items-center gap-1">
                                            <ShieldAlert size={16} /> Exigir monitoramento ativo
                                        </label>
                                    </div>
                                )}
                                
                                {currentUser?.is_super_user && (
                                    <div className="flex items-center gap-2">
                                        <input
                                            id="is_super_user_edit"
                                            type="checkbox"
                                            checked={isSuperUser}
                                            onChange={(e) => {
                                                setIsSuperUser(e.target.checked);
                                                if(e.target.checked) setRequiresMonitoring(false); 
                                            }}
                                            className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-400"
                                            disabled={username === 'admin'}
                                        />
                                        <label htmlFor="is_super_user_edit" className="flex items-center gap-1 text-sm font-medium text-gray-700">
                                            <Star size={16} className="text-yellow-500"/>
                                            É um Super Usuário?
                                        </label>
                                    </div>
                                )}

                                {/* --- CAIXA MULTI-EMPRESA: Visível apenas se isSuperUser = true E quem edita for o 'admin' master --- */}
                                {(isSuperUser && currentUser?.username === 'admin') && (
                                    <div className="md:col-span-2 mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
                                        <label className="block text-sm font-bold text-yellow-800 mb-2">Acesso Multi-empresa (Licenças Consumidas)</label>
                                        <div className="flex flex-wrap gap-4">
                                            {entities.map(ent => (
                                                <label key={ent.id} className="flex items-center gap-2">
                                                    <input 
                                                        type="checkbox"
                                                        checked={allowedEntities.map(String).includes(String(ent.id)) || String(userEntityId) === String(ent.id)}
                                                        onChange={() => handleAllowedEntityChange(ent.id)}
                                                        disabled={String(userEntityId) === String(ent.id)}
                                                        className="w-4 h-4 text-yellow-600 rounded focus:ring-yellow-500"
                                                    />
                                                    <span className="text-sm text-yellow-900">{ent.name}</span>
                                                </label>
                                            ))}
                                        </div>
                                        <p className="text-xs text-yellow-700 mt-2 font-bold">Atenção: O super usuário consumirá 1 licença em cada entidade selecionada acima.</p>
                                    </div>
                                )}
                                {/* --------------------------------------------------- */}

                                <div className="md:col-span-2">
                                     <label className="text-sm font-medium">Moedas Permitidas</label>
                                     <div className="flex flex-wrap gap-4 p-3 mt-1 border rounded-md bg-white">
                                        {(allCurrencies || []).map(currency => (
                                            <label key={currency.code} className="flex items-center gap-2">
                                                <input 
                                                    type="checkbox"
                                                    checked={selectedCurrencies.includes(currency.code)}
                                                    onChange={() => handleCurrencyChange(currency.code)}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                                <span>{currency.name} ({currency.code})</span>
                                            </label>
                                        ))}
                                     </div>
                                </div>
                                
                                <div className="md:col-span-2 pt-4 border-t mt-2">
                                    <h4 className="text-md font-bold mb-3 text-gray-700">Assinatura do Técnico (Laudos Tensão Aplicada)</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded border">
                                        <div>
                                            <label className="text-sm font-medium">Nome na Assinatura</label>
                                            <input type="text" value={signatureName} onChange={e => setSignatureName(e.target.value)} placeholder="Ex: Bruno Braga De Souza" className="w-full p-2 mt-1 border rounded bg-white" />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">Função</label>
                                            <input type="text" value={signatureRole} onChange={e => setSignatureRole(e.target.value)} placeholder="Ex: Técnico em Eletrotécnica" className="w-full p-2 mt-1 border rounded bg-white" />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">Registro (CREA/CRT)</label>
                                            <input type="text" value={signatureRegistry} onChange={e => setSignatureRegistry(e.target.value)} placeholder="Ex: CRT - SP nº 15444951703" className="w-full p-2 mt-1 border rounded bg-white" />
                                        </div>
                                        
                                        <div className="flex flex-col gap-2 mt-4">
                                            <div className="flex items-center gap-2">
                                                <input 
                                                    id="is_signature_public"
                                                    type="checkbox"
                                                    checked={isSignaturePublic}
                                                    onChange={(e) => setIsSignaturePublic(e.target.checked)}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                                <label htmlFor="is_signature_public" className="text-sm font-bold text-gray-700 cursor-pointer">
                                                    Assinatura Pública (Outros podem usar)
                                                </label>
                                            </div>

                                            {/* NOVO CHECKBOX: ENGENHEIRO RESPONSÁVEL */}
                                            <div className="flex items-center gap-2">
                                                <input 
                                                    id="is_responsible_engineer_edit"
                                                    type="checkbox"
                                                    checked={isResponsibleEngineer}
                                                    onChange={(e) => setIsResponsibleEngineer(e.target.checked)}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                                <label htmlFor="is_responsible_engineer_edit" className="text-sm font-bold text-gray-700 cursor-pointer">
                                                    Engenheiro Responsável
                                                </label>
                                            </div>
                                        </div>

                                        <div className="md:col-span-2">
                                            <label className="text-sm font-medium">Imagem da Assinatura (Fundo Transparente Recomendado)</label>
                                            <input type="file" accept="image/png, image/jpeg" onChange={handleSignatureFileChange} className="w-full p-2 mt-1 text-sm border rounded bg-white" />
                                            {(currentSignatureImage || signatureImageBase64) && (
                                                <div className="mt-3 p-3 border rounded bg-white flex flex-col items-center shadow-sm">
                                                    <span className="text-xs text-gray-500 mb-2 font-bold uppercase tracking-wider">Pré-visualização da Assinatura:</span>
                                                    <img src={signatureImageBase64 || currentSignatureImage} alt="Assinatura" style={{ maxHeight: '80px', objectFit: 'contain' }} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                        
                        <div className="flex justify-end p-6 bg-gray-50 border-t">
                            <button type="button" onClick={onClose} className="px-4 py-2 mr-2 font-semibold bg-gray-200 rounded-md">Fechar</button>
                            <button type="submit" disabled={formLoading || !!error} className="px-4 py-2 font-semibold text-white bg-blue-600 rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed">
                                {formLoading ? 'A guardar...' : 'Guardar Alterações'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default EditUserModal;